import { Component, OnDestroy, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {AddRowPopUpComponent} from "./add-row-pop-up/add-row-pop-up.component";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {CounselingInductionService} from "../counseling-induction.service";
import {Observable, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {EmployeeService} from "../../../../../services/employee.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatTableDataSource} from "@angular/material/table";
import {LogService} from "../../../../../services/log.service";

@Component({
  selector: 'app-add-edit-induction',
  templateUrl: './add-edit-induction.component.html',
  styleUrls: ['./add-edit-induction.component.scss']
})
export class AddEditInductionComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  tableForm!: FormGroup;
  // counsellors$: Observable<any[]>;
  inductionId: string | null = null;
  counsellors = new FormControl();
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns = [
    'srNo',
    'topic',
    'inductorBy',
    'date',
    'subTopic',
    'description',
    'feedback',
    'conductedBy',
    'actions'
  ];

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private mEmployeeService: EmployeeService,
    private inductionService: CounselingInductionService,
    private mLogService: LogService,
  ) {
    // this.counsellors$ = this.mEmployeeService.getCounsellers();
  }

  showSnack(message: string) {
    this.snackBar.open(message, 'OK', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['success-snackbar']
    });
  }

  ngOnInit(): void {
    this.tableForm = this.fb.group({
      rows: this.fb.array([])
    });

    // Get ID from URL
    this.inductionId = this.route.snapshot.paramMap.get("id");

    if (this.inductionId) {
      this.loadInductionData(this.inductionId);   // EDIT MODE
    } else {
      this.loadDefaultInductionData();   // CREATE MODE - Load template data
    }
  }

  loadDefaultInductionData() {
    this.inductionService.getAllViewData().pipe(takeUntil(this._destroyed$)).subscribe(data => {
      console.log("Template Induction Data:", data);

      if (data && data.length > 0 && data[0].rows) {
        // Clear existing rows
        this.rows.clear();

        // Patch template data into the form
        data[0].rows.forEach((row: any) => {
          this.rows.push(this.createRow(row));
        });

        // Refresh the table
        this.tableForm.setControl("rows", this.fb.array(this.rows.controls));
      }
    });
  }

  loadInductionData(id: string) {
    this.inductionService.getById(id).pipe(takeUntil(this._destroyed$)).subscribe((doc: any) => {
      if (!doc) return;
      this.counsellors.setValue(doc.inductionFor);
      this.rows.clear();
      const rows = Array.isArray(doc.rows) ? doc.rows : [];
      rows.forEach((row: any) => {
        this.rows.push(this.createRow(row));
      });
      this.tableForm.setControl("rows", this.fb.array(this.rows.controls));
    });
  }

  get rows() {
    return this.tableForm.get('rows') as FormArray;
  }

  createRow(data: any) {
    return this.fb.group({
      orderNo: data.orderNo || 0,
      topic: data.topic || '',
      inductorBy: data.inductorBy || '',
      date: data.date ? (data.date.toDate ? data.date.toDate() : data.date) : '',
      subTopic: data.subTopic || '',
      description: data.description || '',
      feedback: data.feedback || '',
      conductedBy: data.conductedBy || ''
    });
  }

  AddNewRow() {
    // Only check for counsellor selection in EDIT mode
    if (this.inductionId && !this.counsellors.value) {
      alert("Please enter a counsellor name first.");
      return;
    }

    const dialogRef = this.dialog.open(AddRowPopUpComponent, {
      disableClose: true,
      height: '600px',
      width: '900px',
    });

    dialogRef.afterClosed().subscribe(async (rowData) => {
      if (!rowData) return;

      rowData.orderNo = this.rows.length + 1;

      // Add row to form array
      this.rows.push(this.createRow(rowData));
      this.tableForm.setControl('rows', this.fb.array(this.rows.controls));
      this.showSnack("Row added successfully!");
    });
  }

  goBack(){
    this.router.navigate(['/home/counseling/counselling-induction-list']);
  }

  deleteRow(index: number) {
    if (!confirm("Are you sure you want to delete this row?")) return;

    this.rows.removeAt(index);

    // Fix order numbers
    this.rows.controls.forEach((ctrl: any, i: number) => {
      ctrl.patchValue({ orderNo: i + 1 });
    });

    this.showSnack("Row deleted!");

    // Refresh table
    this.tableForm.setControl("rows", this.fb.array(this.rows.controls));
  }

  cellClick(index: number, field: any) {
    this.openEditRowDialog(index, field);
  }

  openEditRowDialog(index: number, focusField?: string) {
    const currentRow = this.rows.at(index).value;

    const dialogRef = this.dialog.open(AddRowPopUpComponent, {
      disableClose: true,
      height: '600px',
      width: '900px',
      data: { ...currentRow, focusField }
    });

    dialogRef.afterClosed().subscribe((updatedRow) => {
      if (!updatedRow) return;

      updatedRow.orderNo = index + 1;

      // Update UI immediately
      this.rows.at(index).patchValue(updatedRow);
      this.tableForm.setControl('rows', this.fb.array(this.rows.controls));

      this.showSnack("Row updated successfully!");
    });
  }

  saveInduction() {
    if (!this.counsellors.value) {
      alert("Please enter a counsellor name first.");
      return;
    }

    const userData = JSON.parse(localStorage.getItem('userData')!);
    const payload = {
      inductionFor: this.counsellors.value,
      submittedBy: userData?.userName || '',
      rows: this.tableForm.value.rows
    };

    if (this.inductionId) {
      // Update existing induction
      this.inductionService.update(this.inductionId, payload)
        .then(() => {
          const activity = {
          date: new Date().getTime(),
          section: 'Counselling Induction',
          action: 'Update',
          description: `Counselling Induction details updated by user`,
          currentIp: localStorage.getItem('currentip')!
        };
          this.mLogService.addLog(activity);
          this.showSnack("Induction Updated Successfully!");
          this.router.navigate(['/home/counseling/counselling-induction-list']);
        });
    } else {
      // Create new induction
      this.inductionService.create(payload)
        .then(() => {
          const activity = {
            date: new Date().getTime(),
            section: 'Counselling Induction',
            action: 'Create',
            description: `Counselling Induction details created by user`,
            currentIp: localStorage.getItem('currentip')!
          };
          this.mLogService.addLog(activity);
          this.showSnack("Induction Saved Successfully!");
          this.router.navigate(['/home/counseling/counselling-induction-list']);
        });
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
